import { NavLink, Outlet, useNavigate } from 'react-router'
import { useEffect, useRef, useState } from 'react'
import {
  Bell,
  Menu,
  Search,
  LayoutDashboard,
  UserRound,
  Handshake,
  Truck,
  CircleAlert,
  HandCoins,
  Package,
  Car,
  TicketPercent,
  PartyPopper,
  ChevronDown,
  LogOut,
  Settings,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Avatar, Badge, SprintRibbon, LanguageSwitcher, LockBadge, cn } from '@waago/ui'
import {
  CURRENT_SPRINT,
  coverageOf,
  hideWhenLocked,
  isUnlocked,
  sprintOf,
  SPRINT_PLAN,
  LOCALE_ENDONYM,
  setLocale,
  stagingActive,
  storedLocale,
  SUPPORTED_LOCALES,
  type Locale,
} from '@waago/api'
import { useAuth, type Role } from '../lib/auth'
import { SprintGate } from './SprintGate'

const LANGUAGES = SUPPORTED_LOCALES.map((code) => ({
  code,
  label: LOCALE_ENDONYM[code],
  coverage: coverageOf(code),
}))

/**
 * Back-office shell.
 *
 * Navigation mirrors the Figma B.O sidebar exactly — see
 * design/png/BO/010_Tableau_de_bord_Desktop_-_95.png. Icon names are the Lucide
 * names the design itself uses (design/ICONS.md).
 */
// `labelKey` rather than `label`: the sidebar is the most visible chrome in
// the app, so it is the first thing that should actually change when somebody
// picks a language. The French text now lives in `locales/fr.json` with the
// rest of the source strings.
type NavItem = { to: string; labelKey: string; icon: typeof LayoutDashboard; roles?: Role[] }

const NAV: NavItem[] = [
  { to: '/', labelKey: 'nav.dashboard', icon: LayoutDashboard },
  { to: '/clients', labelKey: 'nav.clients', icon: UserRound, roles: ['OPS', 'SUPPORT'] },
  {
    to: '/prestataires',
    labelKey: 'nav.providers',
    icon: Handshake,
    roles: ['OPS', 'KYC_REVIEWER'],
  },
  { to: '/missions', labelKey: 'nav.missions', icon: Truck, roles: ['OPS'] },
  { to: '/signalements', labelKey: 'nav.reports', icon: CircleAlert, roles: ['OPS', 'SUPPORT'] },
  { to: '/paiements', labelKey: 'nav.payments', icon: HandCoins, roles: ['FINANCE', 'OPS'] },
  { to: '/services', labelKey: 'nav.services', icon: Package, roles: ['OPS'] },
  { to: '/vehicules', labelKey: 'nav.vehicles', icon: Car, roles: ['OPS'] },
  {
    to: '/abonnements',
    labelKey: 'nav.subscription',
    icon: TicketPercent,
    roles: ['FINANCE', 'MARKETING'],
  },
  {
    to: '/publicite',
    labelKey: 'nav.advertising',
    icon: PartyPopper,
    roles: ['MARKETING', 'OPS'],
  },
]

/**
 * One nav item, drawn the same in the sidebar and in the drawer.
 *
 * Extracted so the two cannot drift: the whole reason the drawer exists is
 * that a phone had no navigation at all, and a drawer showing a different set
 * of links from the sidebar would be its own version of the same bug.
 */
function NavItems({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }) {
  const { t } = useTranslation()
  return (
    <ul>
      {items.map(({ to, labelKey, icon: Icon }) => (
        <li key={to}>
          <NavLink
            to={to}
            end={to === '/'}
            {...(onNavigate ? { onClick: onNavigate } : {})}
            className={({ isActive }) =>
              cn(
                'group flex items-center gap-3 border-l-2 px-4 py-2.5 text-sm',
                'transition-[background-color,color,border-color,padding] duration-[--duration-fast] ease-[--ease-out-soft]',
                isActive
                  ? 'border-brand-600 bg-brand-50 text-brand-600 font-medium'
                  : // Nudges right under the pointer, so the sidebar answers
                    // a hover instead of only recolouring.
                    'border-transparent text-slate-600 hover:bg-slate-50 hover:pl-5',
              )
            }
          >
            <Icon
              className="size-4 shrink-0 transition-transform duration-[--duration-base] ease-[--ease-out-soft] group-hover:scale-110"
              aria-hidden="true"
            />
            <span className="flex-1 truncate">{t(labelKey)}</span>
            {isUnlocked(to) ? null : <LockBadge sprint={sprintOf(to)} />}
          </NavLink>
        </li>
      ))}
    </ul>
  )
}

export function AppShell() {
  const { state, logout, hasRole } = useAuth()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [navOpen, setNavOpen] = useState(false)
  const drawerRef = useRef<HTMLDialogElement>(null)
  const { t } = useTranslation()
  // Device-local for staff: a back-office account is shared far less often than
  // it is used from several machines, and there is no /me/locale for staff.
  const [locale, setLocaleState] = useState<string>(storedLocale())
  const user = state.status === 'authenticated' ? state.user : null

  const visible = NAV.filter((i) => !i.roles || hasRole(...i.roles)).filter(
    (i) => !hideWhenLocked() || isUnlocked(i.to),
  )

  // `showModal()` rather than the `open` attribute, so the drawer gets focus
  // trapping and Escape-to-close from the platform instead of hand-rolled and
  // half-right — the same reason BottomSheet is built on <dialog>.
  useEffect(() => {
    const el = drawerRef.current
    if (!el) return
    if (navOpen && !el.open) el.showModal()
    if (!navOpen && el.open) el.close()
  }, [navOpen])

  return (
    <div className="flex min-h-dvh flex-col bg-slate-50">
      <a
        href="#main"
        className="focus:bg-brand-600 sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded-md focus:px-4 focus:py-2 focus:text-white"
      >
        Aller au contenu principal
      </a>

      {stagingActive() ? (
        <SprintRibbon sprint={CURRENT_SPRINT} days={SPRINT_PLAN[CURRENT_SPRINT].days} />
      ) : null}

      {/* Top bar — dark navy, per the design */}
      <header className="bg-brand-800 sticky top-0 z-30 flex h-16 shrink-0 items-center gap-4 px-4 text-white">
        {/* Below `lg` the sidebar is not rendered at all, which left a phone
            with a dashboard and no way to reach any other screen. This is the
            only way out of that, so it is deliberately the first thing in the
            bar rather than tucked behind the account menu. */}
        <button
          type="button"
          onClick={() => setNavOpen(true)}
          aria-expanded={navOpen}
          aria-controls="nav-drawer"
          aria-label="Ouvrir le menu"
          className="-ml-2 rounded-md p-2 hover:bg-white/10 lg:hidden"
        >
          <Menu className="size-5" aria-hidden="true" />
        </button>

        <div className="flex items-center gap-2 font-bold tracking-tight lg:w-56">
          <span className="text-lg">WAAGO</span>
          <span className="text-2xs hidden font-normal text-white/85 sm:inline">
            Move. Deliver. Connect.
          </span>
        </div>

        <div className="relative mx-auto hidden max-w-xl flex-1 md:block">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-white/75"
            aria-hidden="true"
          />
          <input
            type="search"
            placeholder="Recherche"
            aria-label="Recherche globale"
            className="h-10 w-full rounded-md bg-white/10 pr-3 pl-9 text-sm text-white placeholder:text-white/75 focus:bg-white/15 focus:outline-2 focus:outline-white/40"
          />
        </div>

        {/* `ml-auto` so the bell and the account sit against the right edge.
            On desktop the search box is `flex-1` and already pushes them
            there, so this changes nothing; below `md` the search is hidden and
            without it the two bunched up mid-header with a wide empty stripe
            after them, which read as a broken header rather than a sparse one. */}
        <button
          type="button"
          className="relative ml-auto rounded-md p-2 hover:bg-white/10"
          aria-label="Notifications"
          onClick={() => void navigate('/notifications')}
        >
          <Bell className="size-5" aria-hidden="true" />
        </button>

        {user ? (
          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              className="flex items-center gap-2 rounded-md p-1 pr-2 hover:bg-white/10"
            >
              {/* The name is rendered beside it, so the avatar is decorative. */}
              <Avatar
                name={`${user.firstName} ${user.lastName}`}
                {...(user.avatarUrl ? { src: user.avatarUrl } : {})}
                size="sm"
                decorative
              />
              <span className="hidden text-left leading-tight sm:block">
                <span className="block text-xs font-medium">
                  {user.firstName} {user.lastName}
                </span>
                <span className="text-2xs block text-white/85">{user.roles[0]}</span>
              </span>
              <ChevronDown className="size-4 opacity-70" aria-hidden="true" />
            </button>
            {menuOpen ? (
              <div
                role="menu"
                className="absolute right-0 mt-1 w-52 overflow-hidden rounded-md border border-slate-200 bg-white py-1 text-slate-700 shadow-lg"
              >
                <button
                  role="menuitem"
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50"
                  onClick={() => {
                    setMenuOpen(false)
                    void navigate('/mon-compte')
                  }}
                >
                  <UserRound className="size-4" aria-hidden="true" /> Mon compte
                </button>
                <button
                  role="menuitem"
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50"
                  onClick={() => {
                    setMenuOpen(false)
                    void navigate('/parametres')
                  }}
                >
                  <Settings className="size-4" aria-hidden="true" /> Paramètres
                </button>
                {/* The back-office had no language control at all - the only
                    one in the product was a screen inside the two mobile apps,
                    so staff could not change it. */}
                <div className="border-t border-slate-100 px-3 py-2">
                  <p className="text-2xs mb-1 font-medium text-slate-400 uppercase">
                    {t('language.title')}
                  </p>
                  <LanguageSwitcher
                    compact
                    options={LANGUAGES}
                    current={locale}
                    onSelect={(code) => {
                      setLocale(code as Locale)
                      setLocaleState(code)
                    }}
                    partialLabel={t('language.partial', 'partiel')}
                  />
                </div>
                <button
                  role="menuitem"
                  type="button"
                  className="text-danger flex w-full items-center gap-2 border-t border-slate-100 px-3 py-2 text-sm hover:bg-rose-50"
                  onClick={() => void logout()}
                >
                  <LogOut className="size-4" aria-hidden="true" /> Se déconnecter
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </header>

      <div className="flex flex-1">
        {/* Sidebar */}
        <nav
          aria-label="Navigation principale"
          className="hidden w-56 shrink-0 border-r border-slate-200 bg-white py-2 lg:block"
        >
          <NavItems items={visible} />
        </nav>

        {/* The same navigation, for the screens the sidebar is hidden on. */}
        <dialog
          ref={drawerRef}
          id="nav-drawer"
          onClose={() => setNavOpen(false)}
          onClick={(e) => {
            // Backdrop closes; the panel itself must not.
            if (e.target === drawerRef.current) setNavOpen(false)
          }}
          aria-label="Navigation principale"
          className={cn(
            'mt-0 mr-auto mb-0 ml-0 h-dvh max-h-none w-64 max-w-[85vw] rounded-none bg-white p-0',
            'open:animate-drawer-in open:backdrop:animate-fade-in backdrop:bg-black/40',
            'lg:hidden',
          )}
        >
          <div className="flex h-full flex-col">
            <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3">
              <span className="text-brand-800 font-bold tracking-tight">WAAGO</span>
              <button
                type="button"
                onClick={() => setNavOpen(false)}
                aria-label="Fermer le menu"
                className="grid size-9 place-items-center rounded-full hover:bg-slate-100"
              >
                <X className="size-5 text-slate-700" aria-hidden="true" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto py-2">
              <NavItems items={visible} onNavigate={() => setNavOpen(false)} />
            </div>
          </div>
        </dialog>

        <main id="main" className="min-w-0 flex-1 p-4 lg:p-6">
          {/* The design has no responsive BO layout, and the tables really are
              built for a wide screen — so this stays. What it no longer means
              is "you cannot get anywhere from here": the drawer above covers
              navigation, and this is now only a warning about the tables. */}
          <div className="mb-4 lg:hidden">
            <Badge tone="warning" dot>
              Optimisé pour écran large
            </Badge>
          </div>
          <SprintGate>
            <Outlet />
          </SprintGate>
        </main>
      </div>
    </div>
  )
}
